import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "~/api/errors";
import { reasoningErrorCode } from "~/api/knowitReasoning";
import { useApi } from "~/api/provider";
import {
  REASONING_CHAIN_ERRORS,
  type ReasoningChainView,
  type ReviseReasoningChainInput,
} from "~/api/reasoningTypes";

/** One edit as the editor composes it; the hook adds the session, its revision and the command id. */
export type ChainEdit = Omit<
  ReviseReasoningChainInput,
  "sessionId" | "expectedSessionRevision" | "commandId"
>;
/** An edit that was sent and has not settled: its command id is reused by a retry, never minted again. */
export interface PendingChainEdit {
  commandId: string;
  expectedSessionRevision: number;
  edit: ChainEdit;
}

const message = (error: unknown) => (error instanceof Error ? error.message : String(error));
/** A refusal the server will repeat: nothing to retry. 408 and 429 are the two 4xx worth a retry. */
const isFinalRefusal = (error: unknown) =>
  error instanceof ApiError &&
  error.status >= 400 &&
  error.status < 500 &&
  error.status !== 408 &&
  error.status !== 429;

/**
 * The one owner of a session's chain view: loaded on mount, refreshed after each edit this hook
 * sends, and refreshed when the session's revision moves (another command landed) — never polled
 * on its own; `useReasoningSession` is the clock. An edit is fenced on the revision the chain was
 * read at; a `session-revision-conflict` reloads the chain and raises `conflict` so the editor
 * can say so in one plain sentence. A transient failure keeps the edit `pending` with its
 * command id, so `retry()` replays the same command and the server's receipt keeps it to one step.
 */
export function useReasoningChain({
  sessionId,
  sessionRevision,
  onRevised,
}: {
  sessionId: string;
  sessionRevision: number;
  /** Called after an edit landed, so the owner of the session record can refresh it. */
  onRevised?: () => void;
}) {
  const api = useApi();
  const [chain, setChain] = useState<ReasoningChainView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState<PendingChainEdit | null>(null);
  const [running, setRunning] = useState(false);
  const held = useRef<ReasoningChainView | null>(null);
  const epoch = useRef(0);
  const inFlight = useRef(false);
  const notify = useRef(onRevised);
  notify.current = onRevised;

  const refresh = useCallback(async (): Promise<ReasoningChainView | null> => {
    const mine = epoch.current;
    setLoading(true);
    try {
      const view = await api.reasoning.chain(sessionId);
      if (mine !== epoch.current) return null;
      held.current = view;
      setChain(view);
      setError("");
      return view;
    } catch (cause) {
      if (mine === epoch.current) setError(message(cause));
      return null;
    } finally {
      if (mine === epoch.current) setLoading(false);
    }
  }, [api, sessionId]);

  useEffect(() => {
    epoch.current += 1;
    held.current = null;
    inFlight.current = false;
    setChain(null);
    setPending(null);
    setConflict(false);
    setError("");
    void refresh();
    return () => {
      epoch.current += 1;
    };
  }, [refresh]);

  // Another command moved the session: read the chain again unless the one held is already that new.
  useEffect(() => {
    const view = held.current;
    if (!view || view.sessionRevision >= sessionRevision) return;
    void refresh();
  }, [sessionRevision, refresh]);

  const dispatch = useCallback(
    async (command: PendingChainEdit): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      const mine = epoch.current;
      setPending(command);
      setRunning(true);
      setError("");
      setConflict(false);
      try {
        await api.reasoning.reviseChain({
          ...command.edit,
          sessionId,
          expectedSessionRevision: command.expectedSessionRevision,
          commandId: command.commandId,
        });
        if (mine !== epoch.current) return false;
        setPending(null);
        await refresh();
        notify.current?.();
        return true;
      } catch (cause) {
        if (mine !== epoch.current) return false;
        if (reasoningErrorCode(cause) === REASONING_CHAIN_ERRORS.revisionConflict) {
          setPending(null);
          setConflict(true);
          await refresh();
          return false;
        }
        const text = message(cause);
        if (isFinalRefusal(cause)) {
          // The entry moved under the edit, or the edit itself was refused: reload, nothing to retry.
          setPending(null);
          await refresh();
        }
        // After the reload (which clears a stale load error) the refusal is what the reader needs.
        if (mine === epoch.current) setError(text);
        return false;
      } finally {
        inFlight.current = false;
        if (mine === epoch.current) setRunning(false);
      }
    },
    [api, sessionId, refresh],
  );

  /** Sends a fresh edit, fenced on the revision the chain on screen was read at. */
  const revise = useCallback(
    (edit: ChainEdit) => {
      if (pending) return Promise.resolve(false);
      return dispatch({
        commandId: crypto.randomUUID(),
        expectedSessionRevision: held.current?.sessionRevision ?? sessionRevision,
        edit,
      });
    },
    [dispatch, pending, sessionRevision],
  );
  /** Replays the pending edit with its original command id. */
  const retry = useCallback(
    () => (pending ? dispatch(pending) : Promise.resolve(false)),
    [dispatch, pending],
  );
  const discard = useCallback(() => {
    setPending(null);
    setError("");
  }, []);
  const dismissConflict = useCallback(() => setConflict(false), []);

  return {
    chain,
    loading,
    error,
    /** The last edit hit a stale revision; the chain was reloaded. */
    conflict,
    pending,
    running,
    /** An edit is in flight or waiting for a retry: no second edit until it settles. */
    busy: running || pending !== null,
    refresh,
    revise,
    retry,
    discard,
    dismissConflict,
  };
}
